import { cs } from '../../lib/currency';
/**
 * Public Camp Registration
 *
 * Lets a parent register a child for a camp without first creating an
 * account. On submit, a `parent` account is provisioned with a temporary
 * password (mailed via a welcome email), the child is created, and the
 * visitor is redirected to Stripe Checkout.
 *
 * 3-step flow:
 *   1. Pick camp + week(s)
 *   2. Parent info
 *   3. Child info + review + pay
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Loader2, Calendar, Users, Check, DollarSign, Info, MapPin, Clock, Baby } from 'lucide-react';
import Footer from '../../components/Footer';
import DescriptionView from '../../components/DescriptionView';
import { sanitizeHtml } from '../../utils/sanitize';

const API_URL = (typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL);

const formatMoney = (v) => `${cs()}${Number(v || 0).toFixed(2)}`;

const prettyDate = (iso) => {
  if (!iso) return '';
  try {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
};

const calcAge = (dob) => {
  if (!dob) return null;
  try {
    const d = new Date(dob);
    const t = new Date();
    let age = t.getFullYear() - d.getFullYear();
    const mo = t.getMonth() - d.getMonth();
    if (mo < 0 || (mo === 0 && t.getDate() < d.getDate())) age--;
    return age;
  } catch {
    return null;
  }
};

const calcTotal = (camp, selectedWeeks) => {
  if (!camp) return 0;
  if (camp.weeks && camp.price_per_week && selectedWeeks.length > 0) {
    const n = selectedWeeks.length;
    const base = camp.price_per_week * n;
    const minWeeks = camp.min_weeks_for_discount || 0;
    const type = camp.multi_week_discount_type;
    const val = camp.multi_week_discount_value || 0;
    if (n >= minWeeks && type && val > 0) {
      if (type === 'percentage') return base - base * (val / 100);
      if (type === 'fixed_per_week') return base - val * n;
    }
    return base;
  }
  return camp.fee || 0;
};

const ReadOnlyField = ({ label, value }) => (
  <div>
    <Label className="text-xs uppercase tracking-wide text-slate-500">{label}</Label>
    <div className="mt-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded text-slate-700 text-sm">
      {value || '—'}
    </div>
  </div>
);

export default function PublicCampRegister() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [camps, setCamps] = useState([]);
  const [businessLogo, setBusinessLogo] = useState(null);
  const [businessName, setBusinessName] = useState('the Academy');

  // Step 1
  const [selectedCampId, setSelectedCampId] = useState('');
  const [selectedWeeks, setSelectedWeeks] = useState([]);
  const [payDepositOnly, setPayDepositOnly] = useState(false);
  const [detailsCamp, setDetailsCamp] = useState(null);

  // Authenticated-parent state. When a parent logs in during registration we
  // stop collecting profile info and pull everything from their account +
  // existing children. Token is kept in component state only — no global
  // auth side-effects (parent may be registering from a shared device).
  const [authToken, setAuthToken] = useState(null);
  const [_authUser, setAuthUser] = useState(null);
  const [children, setChildren] = useState([]);
  const [selectedChildId, setSelectedChildId] = useState('');
  // Inline login UI
  const [emailExists, setEmailExists] = useState(false);
  const [emailChecking, setEmailChecking] = useState(false);
  const [loginPassword, setLoginPassword] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);

  // Step 2 — Parent
  const [parent, setParent] = useState({
    parent_name: '',
    parent_email: '',
    parent_phone: '',
    parent_address: '',
    parent_city: '',
    parent_state: '',
    parent_zip: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
  });

  // Step 3 — Child + Review
  const [child, setChild] = useState({
    player_name: '',
    player_dob: '',
    player_gender: '',
    player_school: '',
    player_grade: '',
    player_tshirt_size: '',
    experience_level: '',
    medical_notes: '',
    dietary_restrictions: '',
    allergies: '',
    additional_info: '',
  });
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const selectedCamp = camps.find((c) => c.id === selectedCampId);
  const total = selectedCamp ? calcTotal(selectedCamp, selectedWeeks) : 0;
  const depositAmount = selectedCamp?.deposit_amount || 0;
  const chargeAmount = payDepositOnly && depositAmount > 0 ? depositAmount : total;

  useEffect(() => {
    const load = async () => {
      try {
        // NOTE: `/api/business-config/public` doesn't exist — use the two
        // real public endpoints instead. `/api/admin/logo` is public-safe
        // (no auth dependency) and returns `logo_url`.
        const [campsRes, logoRes, contactRes] = await Promise.allSettled([
          axios.get(`${API_URL}/api/camps/active`),
          axios.get(`${API_URL}/api/admin/logo`),
          axios.get(`${API_URL}/api/contact-info`),
        ]);
        if (campsRes.status === 'fulfilled') {
          setCamps(campsRes.value.data || []);
        }
        if (logoRes.status === 'fulfilled' && logoRes.value?.data?.logo_url) {
          // `/api/admin/logo` returns a path like "/api/admin/logo/{id}" —
          // prefix it with API_URL so the <img> points at the backend.
          const url = logoRes.value.data.logo_url;
          setBusinessLogo(url.startsWith('http') ? url : `${API_URL}${url}`);
        }
        if (contactRes.status === 'fulfilled' && contactRes.value?.data?.business_name) {
          setBusinessName(contactRes.value.data.business_name);
        }
      } catch (e) {
        toast.error('Failed to load camps');
      } finally {
        setLoading(false);
      }
    };
    load();
    // If URL has ?camp=<id>, pre-select it
    const params = new URLSearchParams(window.location.search);
    const preCamp = params.get('camp');
    if (preCamp) setSelectedCampId(preCamp);
    if (params.get('cancelled') === '1') {
      toast.info('Payment cancelled. You can retry below.');
    }
  }, []);

  const toggleWeek = (weekNum) => {
    setSelectedWeeks((prev) =>
      prev.includes(weekNum) ? prev.filter((w) => w !== weekNum) : [...prev, weekNum].sort((a, b) => a - b)
    );
  };

  // When the parent types an email on Step 2, ask the backend whether an
  // account already exists. If yes, surface an inline login form so they can
  // continue without re-entering their profile and child data.
  const checkEmailOnBlur = async () => {
    const email = parent.parent_email.trim().toLowerCase();
    if (!email || !/^\S+@\S+\.\S+$/.test(email) || authToken) {
      setEmailExists(false);
      return;
    }
    setEmailChecking(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/camps/public/check-email`, {
        params: { email },
      });
      setEmailExists(!!data?.exists);
    } catch {
      setEmailExists(false);
    } finally {
      setEmailChecking(false);
    }
  };

  const handleInlineLogin = async () => {
    const email = parent.parent_email.trim().toLowerCase();
    if (!email || !loginPassword) {
      toast.error('Enter your password to log in');
      return;
    }
    setLoggingIn(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/login`, {
        email,
        password: loginPassword,
      });
      const tok = data?.access_token || data?.token;
      if (!tok) throw new Error('No token in response');
      setAuthToken(tok);
      setAuthUser(data.user || { email, name: data?.user?.name });
      setLoginPassword('');
      // Pull this parent's children so they can pick one on Step 3 instead
      // of retyping the child's entire profile.
      const kidsRes = await axios.get(`${API_URL}/api/players/my-children`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      const kids = Array.isArray(kidsRes.data) ? kidsRes.data : [];
      setChildren(kids);
      // Prefill parent form too, so the review card shows the right name.
      setParent((p) => ({
        ...p,
        parent_name: data?.user?.name || p.parent_name,
        parent_email: email,
      }));
      if (kids.length > 0) {
        // Auto-select the only child if there's just one; otherwise let
        // them pick on Step 3.
        if (kids.length === 1) setSelectedChildId(kids[0].id);
      }
      toast.success('Logged in — we\'ll prefill your info');
      // Skip the profile step since we already have everything.
      setStep(3);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Login failed. Check your password.');
    } finally {
      setLoggingIn(false);
    }
  };

  // When the authenticated parent picks a child from their list, copy the
  // player's stored profile into `child` state. Step 3 renders these as
  // read-only when `authToken` is set.
  const selectChild = (pid) => {
    setSelectedChildId(pid);
    const k = children.find((c) => c.id === pid);
    if (!k) return;
    setChild({
      player_name: k.full_name || '',
      player_dob: k.date_of_birth || '',
      player_gender: k.gender || '',
      player_school: k.school || '',
      player_grade: k.grade || '',
      player_tshirt_size: k.tshirt_size || '',
      experience_level: k.skill_level || '',
      medical_notes: k.medical_notes || '',
      dietary_restrictions: k.dietary_restrictions || '',
      allergies: k.allergies || '',
      additional_info: '',
    });
  };

  const canProceedStep1 = () => {
    if (!selectedCampId) return false;
    if (selectedCamp?.weeks?.length > 0 && selectedWeeks.length === 0) return false;
    return true;
  };

  const canProceedStep2 = () => {
    // Authenticated parent skipped this step via login.
    if (authToken) return true;
    const emailOk = /^\S+@\S+\.\S+$/.test(parent.parent_email.trim());
    return parent.parent_name.trim() && emailOk && parent.parent_phone.trim();
  };

  const canSubmit = () => {
    if (authToken) {
      // Authenticated flow: must have picked an existing child.
      return !!selectedChildId && agreedToTerms;
    }
    return child.player_name.trim() && agreedToTerms;
  };

  const handleSubmit = async () => {
    if (!canSubmit()) {
      toast.error(
        authToken
          ? 'Please select your child and accept terms'
          : 'Please complete required fields and accept terms'
      );
      return;
    }
    setSubmitting(true);
    try {
      const base = {
        camp_id: selectedCampId,
        selected_weeks: selectedWeeks,
        pay_deposit_only: payDepositOnly,
        agreed_to_terms: true,
      };
      const payload = authToken
        ? { ...base, player_id: selectedChildId }
        : {
            ...base,
            ...parent,
            parent_email: parent.parent_email.trim().toLowerCase(),
            ...child,
            player_dob: child.player_dob || null,
          };
      const { data } = await axios.post(
        `${API_URL}/api/camps/public/register-and-checkout`,
        payload,
        authToken ? { headers: { Authorization: `Bearer ${authToken}` } } : undefined
      );
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error('No checkout URL received. Please try again.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-camp-register-page">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-3 hover:opacity-80"
            data-testid="back-to-home"
          >
            {businessLogo ? (
              <img src={businessLogo} alt={businessName} className="h-10 w-auto" />
            ) : (
              <span className="font-bold text-lg">{businessName}</span>
            )}
          </button>
          <Button variant="ghost" onClick={() => navigate('/')}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Home
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Progress indicator */}
        <div className="mb-6 flex items-center justify-between gap-2">
          {[
            { n: 1, label: 'Camp' },
            { n: 2, label: 'Parent' },
            { n: 3, label: 'Child & Pay' },
          ].map((s, idx, arr) => (
            <div key={s.n} className="flex-1 flex items-center">
              <div
                className={`flex items-center gap-2 ${
                  step >= s.n ? 'text-emerald-700' : 'text-slate-400'
                }`}
                data-testid={`step-indicator-${s.n}`}
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    step >= s.n
                      ? 'bg-emerald-600 text-white'
                      : 'bg-slate-200 text-slate-500'
                  }`}
                >
                  {step > s.n ? <Check className="h-4 w-4" /> : s.n}
                </div>
                <span className="font-medium text-sm hidden sm:inline">{s.label}</span>
              </div>
              {idx < arr.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 ${step > s.n ? 'bg-emerald-600' : 'bg-slate-200'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Step 1: Pick camp */}
        {step === 1 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-emerald-600" />
                Select a Camp
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {camps.length === 0 ? (
                <p className="text-slate-500 py-6 text-center">
                  No camps are open for registration right now. Check back soon!
                </p>
              ) : (
                camps.map((camp) => {
                  const isSelected = selectedCampId === camp.id;
                  const full = camp.max_participants && camp.registered_count >= camp.max_participants;
                  return (
                    <div
                      key={camp.id}
                      className={`border-2 rounded-lg p-4 cursor-pointer transition ${
                        isSelected
                          ? 'border-emerald-500 bg-emerald-50'
                          : full
                          ? 'border-slate-200 opacity-50 cursor-not-allowed'
                          : 'border-slate-200 hover:border-emerald-300'
                      }`}
                      onClick={() => {
                        if (full) return;
                        setSelectedCampId(camp.id);
                        setSelectedWeeks([]);
                      }}
                      data-testid={`camp-option-${camp.id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-lg">{camp.name}</h3>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                              onClick={(e) => { e.stopPropagation(); setDetailsCamp(camp); }}
                              data-testid={`public-camp-details-${camp.id}`}
                            >
                              <Info className="h-3.5 w-3.5 mr-1" />
                              Details
                            </Button>
                          </div>
                          <p className="text-sm text-slate-500 mt-1">
                            {prettyDate(camp.start_date)} — {prettyDate(camp.end_date)}
                            {camp.location && ` · ${camp.location}`}
                          </p>
                          {camp.min_age != null && camp.max_age != null && (
                            <p className="text-xs text-slate-500 mt-1">
                              Ages {camp.min_age}–{camp.max_age}
                            </p>
                          )}
                          <div className="flex items-center gap-2 mt-2">
                            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                              {camp.price_per_week
                                ? `${formatMoney(camp.price_per_week)} / week`
                                : formatMoney(camp.fee)}
                            </Badge>
                            {camp.deposit_amount > 0 && (
                              <Badge variant="outline" className="text-xs">
                                Deposit: {formatMoney(camp.deposit_amount)}
                              </Badge>
                            )}
                            {full && <Badge variant="destructive">Full</Badge>}
                          </div>
                        </div>
                        {isSelected && <Check className="h-6 w-6 text-emerald-600 flex-shrink-0" />}
                      </div>

                      {/* Show weeks picker when selected */}
                      {isSelected && camp.weeks?.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-emerald-200">
                          <Label className="text-sm font-medium text-slate-700 mb-2 block">
                            Select week(s) to attend
                          </Label>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            {camp.weeks.map((w) => (
                              <div
                                key={w.week_number}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleWeek(w.week_number);
                                }}
                                className={`p-2 border rounded cursor-pointer text-sm ${
                                  selectedWeeks.includes(w.week_number)
                                    ? 'border-emerald-500 bg-white font-medium'
                                    : 'border-slate-200 bg-white hover:border-emerald-300'
                                }`}
                                data-testid={`week-option-${camp.id}-${w.week_number}`}
                              >
                                <div className="font-medium">Week {w.week_number}</div>
                                <div className="text-xs text-slate-500">
                                  {prettyDate(w.start_date)} – {prettyDate(w.end_date)}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 2: Parent info */}
        {step === 2 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-emerald-600" />
                Parent / Guardian Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {emailExists && !authToken && (
                <div
                  className="border border-blue-200 bg-blue-50 rounded-lg p-4 space-y-3"
                  data-testid="inline-login-banner"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-blue-900">
                        Looks like you already have an account
                      </p>
                      <p className="text-xs text-blue-700 mt-1">
                        Log in and we'll prefill your info and children — no need to retype anything.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      type="password"
                      placeholder="Enter your password"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleInlineLogin();
                      }}
                      className="bg-white"
                      data-testid="inline-login-password"
                    />
                    <Button
                      type="button"
                      onClick={handleInlineLogin}
                      disabled={loggingIn || !loginPassword}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="inline-login-submit"
                    >
                      {loggingIn ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Log in'}
                    </Button>
                  </div>
                  <p className="text-xs text-blue-700">
                    Prefer to register as a guest?{' '}
                    <button
                      type="button"
                      className="underline font-medium"
                      onClick={() => setEmailExists(false)}
                      data-testid="dismiss-login-banner"
                    >
                      Use a different email
                    </button>
                    .
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Full Name *</Label>
                  <Input
                    value={parent.parent_name}
                    onChange={(e) => setParent({ ...parent, parent_name: e.target.value })}
                    data-testid="parent-name"
                  />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={parent.parent_email}
                    onChange={(e) => setParent({ ...parent, parent_email: e.target.value })}
                    onBlur={checkEmailOnBlur}
                    data-testid="parent-email"
                  />
                  {emailChecking && (
                    <p className="text-xs text-slate-400 mt-1">Checking…</p>
                  )}
                </div>
                <div>
                  <Label>Phone *</Label>
                  <Input
                    type="tel"
                    value={parent.parent_phone}
                    onChange={(e) => setParent({ ...parent, parent_phone: e.target.value })}
                    data-testid="parent-phone"
                  />
                </div>
                <div>
                  <Label>Street Address</Label>
                  <Input
                    value={parent.parent_address}
                    onChange={(e) => setParent({ ...parent, parent_address: e.target.value })}
                    data-testid="parent-address"
                  />
                </div>
                <div>
                  <Label>City</Label>
                  <Input
                    value={parent.parent_city}
                    onChange={(e) => setParent({ ...parent, parent_city: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>State</Label>
                    <Input
                      value={parent.parent_state}
                      onChange={(e) => setParent({ ...parent, parent_state: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Zip</Label>
                    <Input
                      value={parent.parent_zip}
                      onChange={(e) => setParent({ ...parent, parent_zip: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <Label>Emergency Contact Name</Label>
                  <Input
                    value={parent.emergency_contact_name}
                    onChange={(e) => setParent({ ...parent, emergency_contact_name: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Emergency Contact Phone</Label>
                  <Input
                    type="tel"
                    value={parent.emergency_contact_phone}
                    onChange={(e) => setParent({ ...parent, emergency_contact_phone: e.target.value })}
                  />
                </div>
              </div>
              <p className="text-xs text-slate-500 pt-2 border-t">
                We'll create a parent account and email you a temporary password so you can
                manage this and future registrations online.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Child info + pay */}
        {step === 3 && (
          <div className="space-y-4">
            {/* Authenticated parent — pick one of their existing children */}
            {authToken && (
              <Card>
                <CardHeader>
                  <CardTitle>Select Your Child</CardTitle>
                </CardHeader>
                <CardContent>
                  {children.length === 0 ? (
                    <div className="text-sm text-slate-600 bg-amber-50 border border-amber-200 rounded p-3">
                      Your account doesn't have any children linked yet. Please{' '}
                      <a href="/parent/my-family" className="underline font-medium">
                        add a child to your family
                      </a>{' '}
                      first, then return here.
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {children.map((kid) => {
                        const isSel = selectedChildId === kid.id;
                        return (
                          <button
                            key={kid.id}
                            type="button"
                            onClick={() => selectChild(kid.id)}
                            className={`p-3 text-left border rounded-lg transition ${
                              isSel
                                ? 'border-emerald-500 bg-emerald-50 ring-1 ring-emerald-300'
                                : 'border-slate-200 hover:border-emerald-300'
                            }`}
                            data-testid={`child-option-${kid.id}`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="font-medium text-slate-900">{kid.full_name}</div>
                                <div className="text-xs text-slate-500 mt-0.5">
                                  {kid.date_of_birth
                                    ? `Age ${calcAge(kid.date_of_birth)}`
                                    : 'DOB not set'}
                                  {kid.grade ? ` • Grade ${kid.grade}` : ''}
                                </div>
                              </div>
                              {isSel && <Check className="h-5 w-5 text-emerald-600" />}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {selectedChildId && (
                    <p className="text-xs text-slate-500 mt-3">
                      Personal details are pulled from this child's profile and shown below for your
                      review. To edit, please update the profile in{' '}
                      <a href="/parent/my-family" className="underline">
                        My Family
                      </a>
                      .
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle>Child Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {authToken ? (
                  // Authenticated flow — locked summary pulled from the
                  // selected child's profile. Edits happen in My Family.
                  !selectedChildId ? (
                    <p className="text-sm text-slate-500">
                      Pick your child above to see their information here.
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      <ReadOnlyField label="Full Name" value={child.player_name} />
                      <ReadOnlyField
                        label="Date of Birth"
                        value={
                          child.player_dob
                            ? `${child.player_dob} (age ${calcAge(child.player_dob) ?? '—'})`
                            : '—'
                        }
                      />
                      <ReadOnlyField label="Gender" value={child.player_gender || '—'} />
                      <ReadOnlyField
                        label="Experience Level"
                        value={child.experience_level || '—'}
                      />
                      <ReadOnlyField label="School" value={child.player_school || '—'} />
                      <ReadOnlyField label="Grade" value={child.player_grade || '—'} />
                      <ReadOnlyField
                        label="T-Shirt Size"
                        value={child.player_tshirt_size || '—'}
                      />
                      <ReadOnlyField label="Allergies" value={child.allergies || '—'} />
                      <ReadOnlyField
                        label="Dietary Restrictions"
                        value={child.dietary_restrictions || '—'}
                      />
                      <div className="md:col-span-2">
                        <ReadOnlyField
                          label="Medical Notes"
                          value={child.medical_notes || '—'}
                        />
                      </div>
                    </div>
                  )
                ) : (
                <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Child's Full Name *</Label>
                    <Input
                      value={child.player_name}
                      onChange={(e) => setChild({ ...child, player_name: e.target.value })}
                      data-testid="player-name"
                    />
                  </div>
                  <div>
                    <Label>Date of Birth</Label>
                    <Input
                      type="date"
                      value={child.player_dob}
                      onChange={(e) => setChild({ ...child, player_dob: e.target.value })}
                      data-testid="player-dob"
                    />
                    {child.player_dob && (
                      <p className="text-xs text-slate-500 mt-1">Age: {calcAge(child.player_dob)}</p>
                    )}
                  </div>
                  <div>
                    <Label>Gender</Label>
                    <Select
                      value={child.player_gender}
                      onValueChange={(v) => setChild({ ...child, player_gender: v })}
                    >
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                        <SelectItem value="prefer_not">Prefer not to say</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Experience Level</Label>
                    <Select
                      value={child.experience_level}
                      onValueChange={(v) => setChild({ ...child, experience_level: v })}
                    >
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="beginner">Beginner</SelectItem>
                        <SelectItem value="intermediate">Intermediate</SelectItem>
                        <SelectItem value="advanced">Advanced</SelectItem>
                        <SelectItem value="competitive">Competitive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>School</Label>
                    <Input
                      value={child.player_school}
                      onChange={(e) => setChild({ ...child, player_school: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Grade</Label>
                    <Input
                      value={child.player_grade}
                      onChange={(e) => setChild({ ...child, player_grade: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>T-Shirt Size</Label>
                    <Select
                      value={child.player_tshirt_size}
                      onValueChange={(v) => setChild({ ...child, player_tshirt_size: v })}
                    >
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="YS">Youth Small</SelectItem>
                        <SelectItem value="YM">Youth Medium</SelectItem>
                        <SelectItem value="YL">Youth Large</SelectItem>
                        <SelectItem value="AS">Adult Small</SelectItem>
                        <SelectItem value="AM">Adult Medium</SelectItem>
                        <SelectItem value="AL">Adult Large</SelectItem>
                        <SelectItem value="AXL">Adult XL</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Allergies (if any)</Label>
                  <Input
                    value={child.allergies}
                    onChange={(e) => setChild({ ...child, allergies: e.target.value })}
                    placeholder="e.g., peanuts, bee stings"
                  />
                </div>
                <div>
                  <Label>Dietary Restrictions</Label>
                  <Input
                    value={child.dietary_restrictions}
                    onChange={(e) => setChild({ ...child, dietary_restrictions: e.target.value })}
                    placeholder="e.g., vegetarian, halal"
                  />
                </div>
                <div>
                  <Label>Medical Notes</Label>
                  <Textarea
                    value={child.medical_notes}
                    onChange={(e) => setChild({ ...child, medical_notes: e.target.value })}
                    rows={2}
                  />
                </div>
                {selectedCamp?.additional_info_label && (
                  <div>
                    <Label>{selectedCamp.additional_info_label}</Label>
                    <Textarea
                      value={child.additional_info}
                      onChange={(e) => setChild({ ...child, additional_info: e.target.value })}
                      rows={2}
                    />
                  </div>
                )}
                </>
                )}
              </CardContent>
            </Card>

            {/* Review + Pay */}
            <Card className="border-emerald-200 bg-emerald-50/40">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-emerald-600" />
                  Review & Payment
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Camp</span>
                  <span className="font-medium">{selectedCamp?.name}</span>
                </div>
                {selectedWeeks.length > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Weeks</span>
                    <span className="font-medium">
                      {selectedWeeks.sort((a, b) => a - b).map((w) => `W${w}`).join(', ')}
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Player</span>
                  <span className="font-medium">{child.player_name || '—'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Parent</span>
                  <span className="font-medium">{parent.parent_name}</span>
                </div>
                <div className="pt-2 border-t flex justify-between">
                  <span className="font-semibold">Total</span>
                  <span className="font-bold text-lg">{formatMoney(total)}</span>
                </div>

                {depositAmount > 0 && (
                  <label className="flex items-start gap-2 pt-2 cursor-pointer">
                    <Checkbox
                      checked={payDepositOnly}
                      onCheckedChange={(v) => setPayDepositOnly(!!v)}
                      data-testid="pay-deposit-toggle"
                    />
                    <div className="text-sm">
                      <div className="font-medium">Pay deposit only now</div>
                      <div className="text-xs text-slate-500">
                        Reserve with {formatMoney(depositAmount)} now; pay remaining {formatMoney(total - depositAmount)} later.
                      </div>
                    </div>
                  </label>
                )}

                <div className="bg-white rounded-lg p-3 border text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">You'll be charged now</span>
                    <span className="font-bold text-xl text-emerald-700">{formatMoney(chargeAmount)}</span>
                  </div>
                </div>

                {selectedCamp?.terms_and_conditions && (
                  <div
                    className="text-xs text-slate-600 bg-white p-3 rounded border max-h-40 overflow-y-auto"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(selectedCamp.terms_and_conditions) }}
                  />
                )}

                <label className="flex items-start gap-2 cursor-pointer">
                  <Checkbox
                    checked={agreedToTerms}
                    onCheckedChange={(v) => setAgreedToTerms(!!v)}
                    data-testid="agreed-to-terms"
                  />
                  <span className="text-sm">
                    I agree to the terms and conditions and consent to account creation at{' '}
                    <strong>{parent.parent_email || 'my email'}</strong>.
                  </span>
                </label>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Nav buttons */}
        <div className="mt-6 flex justify-between gap-2">
          <Button
            variant="outline"
            onClick={() => setStep((s) => Math.max(1, s - 1))}
            disabled={step === 1 || submitting}
            data-testid="step-back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
          {step < 3 ? (
            <Button
              onClick={() => setStep((s) => s + 1)}
              disabled={(step === 1 && !canProceedStep1()) || (step === 2 && !canProceedStep2())}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="step-next-btn"
            >
              Next <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting || !canSubmit()}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="submit-registration-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating checkout…
                </>
              ) : (
                <>Pay {formatMoney(chargeAmount)} & Register</>
              )}
            </Button>
          )}
        </div>
      </main>

      {/* Full details dialog (read-only) */}
      <Dialog open={!!detailsCamp} onOpenChange={(v) => !v && setDetailsCamp(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="public-camp-details-dialog">
          <DialogHeader>
            <DialogTitle>{detailsCamp?.name}</DialogTitle>
            {detailsCamp?.subtitle && (
              <DialogDescription>{detailsCamp.subtitle}</DialogDescription>
            )}
          </DialogHeader>
          {detailsCamp && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2 text-slate-700">
                  <Calendar className="h-4 w-4 text-blue-600 shrink-0" />
                  <span>{prettyDate(detailsCamp.start_date)} — {prettyDate(detailsCamp.end_date)}</span>
                </div>
                {(detailsCamp.start_time || detailsCamp.end_time) && (
                  <div className="flex items-center gap-2 text-slate-700">
                    <Clock className="h-4 w-4 text-purple-600 shrink-0" />
                    <span>{detailsCamp.start_time} – {detailsCamp.end_time}</span>
                  </div>
                )}
                {detailsCamp.location && (
                  <div className="flex items-center gap-2 text-slate-700 sm:col-span-2">
                    <MapPin className="h-4 w-4 text-rose-600 shrink-0" />
                    <span>{detailsCamp.location}</span>
                  </div>
                )}
                {(detailsCamp.min_age != null || detailsCamp.max_age != null) && (
                  <div className="flex items-center gap-2 text-slate-700">
                    <Baby className="h-4 w-4 text-pink-600 shrink-0" />
                    <span>Ages: {detailsCamp.min_age ?? 'Any'} – {detailsCamp.max_age ?? 'Any'}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-slate-700">
                  <DollarSign className="h-4 w-4 text-emerald-600 shrink-0" />
                  <span className="font-semibold text-emerald-700">
                    {detailsCamp.price_per_week
                      ? `${formatMoney(detailsCamp.price_per_week)} / week`
                      : formatMoney(detailsCamp.fee)}
                  </span>
                </div>
              </div>
              {detailsCamp.weeks?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Available weeks</p>
                  <div className="flex flex-wrap gap-1.5">
                    {detailsCamp.weeks.map((w, idx) => (
                      <Badge key={w.week_number || idx} variant="outline" className="text-xs">
                        {w.label || `W${w.week_number || idx + 1}: ${prettyDate(w.start_date)}`}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              {detailsCamp.deposit_amount > 0 && (
                <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                  {formatMoney(detailsCamp.deposit_amount)} deposit to reserve
                </Badge>
              )}
              {detailsCamp.description && (
                <div className="border-t pt-4">
                  <p className="text-xs font-semibold text-slate-500 uppercase mb-2">About this camp</p>
                  <DescriptionView
                    value={detailsCamp.description}
                    className="text-sm text-slate-700"
                  />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailsCamp(null)}>Close</Button>
            <Button
              disabled={!detailsCamp || (detailsCamp.max_participants && detailsCamp.registered_count >= detailsCamp.max_participants)}
              onClick={() => {
                const c = detailsCamp;
                setDetailsCamp(null);
                setSelectedCampId(c.id);
                setSelectedWeeks([]);
              }}
              data-testid="public-camp-details-select-btn"
            >
              Select this camp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
